<template>
  <div class="g-page">
    <div class="content-container">

      <!-- HEADER QISMI -->
      <div class="header-row">
        <h2 class="page-title">{{ $t($route.name) || 'Buyurtmalar' }}</h2>
        <div class="header-actions">

          <!-- Qidiruv -->
          <div class="search-wrapper">
            <b-icon icon="search" class="search-icon"></b-icon>
            <input v-model="searchQuery" type="search" :placeholder="$t('Qidiruv...')" class="modern-search">
          </div>

          <!-- Status filter (segmented) -->
          <div class="status-filter">
            <button :class="['filter-btn', { active: filterStatus === 'all' }]" @click="filterStatus = 'all'">{{ $t('Barchasi') }}</button>
            <button v-for="s in statusDefs" :key="s.value"
              :class="['filter-btn', { active: filterStatus === s.value }]" @click="filterStatus = s.value">
              {{ s.label }}
            </button>
          </div>

          <!-- Yangilash -->
          <button class="refresh-btn" @click="fetchOrders" title="Yangilash">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>

          <!-- Yangi buyurtma -->
          <button class="action-btn new-order-btn" @click="openNewOrderModal">
            <b-icon icon="plus-circle-fill"></b-icon>
            <span>{{ $t("Yangi buyurtma") }}</span>
          </button>

          <Top />
        </div>
      </div>

      <!-- JADVAL QISMI -->
      <div class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width: 60px">№</th>
                <th>SANA</th>
                <th>MIJOZ / TEL</th>
                <th>MANZIL</th>
                <th>SUMMA</th>
                <th>STATUS</th>
                <th style="width: 150px; text-align: center;">AMAL</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in filteredOrders" >
                <!-- ASOSIY QATOR -->
                <tr :key="'r-' + item.id" @click="toggleExpand(item.id)" class="main-row" :class="{ 'expanded': expandedId === item.id }">
                  <td><span class="id-badge">{{ item.id }}</span></td>

                  <td class="text-dark font-bold">{{ formatDate(item.created_at) }}</td>

                  <td>
                    <div class="font-bold text-dark">{{ item.kontragent ? item.kontragent.name : '-' }}</div>
                    <div class="text-muted small">{{ item.kontragent ? item.kontragent.phone_number : item.phone_number }}</div>
                  </td>

                  <td>
                    <span class="text-muted small">{{ item.address || '-' }}</span>
                  </td>

                  <td class="price blue">{{ formatMoney(item.summa) }}</td>

                  <td @click.stop>
                    <!-- "Pul to'landi" — avtomatik (savdoga jo'natilib puli to'langanda) qo'yiladi, qo'lda o'zgartirilmaydi -->
                    <span
                      v-if="item.status === 'paid'"
                      class="status-badge st-paid"
                      title="Savdoga jo'natilib puli to'langan"
                    >
                      {{ getStatusLabel('paid') }}
                    </span>
                    <select
                      v-else
                      class="status-select-inline"
                      :class="statusClass(item.status)"
                      :value="item.status || 'new'"
                      @change="changeStatus(item, $event.target.value)"
                      title="Statusni o'zgartirish"
                    >
                      <option v-for="s in selectableStatuses" :key="s.value" :value="s.value">{{ s.label }}</option>
                    </select>
                  </td>

                  <td>
                    <div class="actions">
                      <!-- Faqat hali savdosi yaratilmagan tugallangan buyurtmani jo'natish -->
                      <button
                        v-if="item.status === 'completed' && !Number(item.has_sale)"
                        class="action-btn pos-btn"
                        @click.stop="goToPOS(item)"
                        title="Savdoga jo'natish (POS)"
                      >
                        <b-icon icon="cart-check-fill"></b-icon>
                        <span>Savdoga jo'natish</span>
                      </button>
                      <button class="icon-btn view" @click.stop="toggleExpand(item.id)" title="Ko'rish / Yopish">
                        <b-icon :icon="expandedId === item.id ? 'chevron-up' : 'chevron-down'"></b-icon>
                      </button>
                      <button class="icon-btn print" @click.stop="printOrder(item)" title="Pechat (narxsiz)">
                        <b-icon icon="printer"></b-icon>
                      </button>
                      <button class="icon-btn del" @click.stop="openDeleteModal(item)" title="O'chirish">
                        <b-icon icon="trash"></b-icon>
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- KENGAYTIRILGAN QATOR - mahsulotlar ro'yxati -->
                <tr v-if="expandedId === item.id" :key="'d-' + item.id" class="detail-row">
                  <td colspan="7">
                    <div class="detail-container">

                      <!-- Izoh (xizmatlar bilan) -->
                      <div v-if="item.comment" class="comment-block">
                        <b-icon icon="chat-left-text" class="mr-1"></b-icon>
                        <span>{{ item.comment }}</span>
                      </div>

                      <!-- Mahsulotlar -->
                      <div v-if="item.products && item.products.length > 0" class="products-list">
                        <div class="products-header">
                          <b-icon icon="box-seam" class="mr-1"></b-icon>
                          Buyurtma qilingan mahsulotlar ({{ item.products.length }} ta)
                        </div>
                        <table class="inner-table">
                          <thead>
                            <tr>
                              <th>Mahsulot</th>
                              <th>Soni</th>
                              <th>Narxi</th>
                              <th>Jami</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="p in item.products" :key="p.id">
                              <td>{{ p.product ? (p.product.extra_name || p.product.name) : 'Noma\'lum' }}</td>
                              <td>{{ p.count }}</td>
                              <td>{{ formatMoney(p.price) }}</td>
                              <td class="font-bold">{{ formatMoney(p.summa) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div v-if="!item.products || item.products.length === 0" class="no-products">
                        Mahsulotlar yo'q
                      </div>

                      <!-- Xizmatlar -->
                      <div v-if="item.services && item.services.length > 0" class="products-list" style="margin-top: 12px;">
                        <div class="products-header" style="color: #a142f4;">
                          <b-icon icon="gear" class="mr-1"></b-icon>
                          Xizmatlar ({{ item.services.length }} ta)
                        </div>
                        <table class="inner-table">
                          <thead>
                            <tr>
                              <th>Xizmat nomi</th>
                              <th>Ish haqi</th>
                              <th>Peredelka</th>
                              <th>Jami</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="s in item.services" :key="s.id">
                              <td>{{ s.name }}</td>
                              <td>{{ formatMoney(s.labor_price) }}</td>
                              <td>{{ formatMoney(s.redo_price) }}</td>
                              <td class="font-bold">{{ formatMoney(Number(s.labor_price || 0) + Number(s.redo_price || 0)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                    </div>
                  </td>
                </tr>
              </template>

              <!-- BO'SH HOLAT -->
              <tr v-if="!loading && filteredOrders.length === 0">
                <td colspan="7" class="empty-row">
                  <div class="py-5 text-center">
                    <p>Ma'lumot topilmadi</p>
                  </div>
                </td>
              </tr>

              <!-- LOADING -->
              <tr v-if="loading">
                <td colspan="7" class="text-center py-5">
                  <b-spinner variant="primary"></b-spinner>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- YANGI BUYURTMA MODALI (alohida komponent) -->
    <ZakazForm
      :show="showNewOrderModal"
      @close="showNewOrderModal = false"
      @created="fetchOrders"
    />

    <!-- O'CHIRISH MODALI -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal-box animate-pop">
        <div class="warning-icon">!</div>
        <h3>O'chirishni tasdiqlaysizmi?</h3>
        <p v-if="selectedOrder" class="text-secondary mt-2">
          <b>#{{ selectedOrder.id }}</b> buyurtma tizimdan o'chiriladi.
        </p>
        <div class="modal-btns">
          <button class="m-btn cancel" @click="closeDeleteModal">YO'Q</button>
          <button class="m-btn confirm" @click="deleteItem">HA, O'CHIRILSIN</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Top from '../Layout/Top.vue';
import ZakazForm from './Form.vue';

export default {
  name: 'ZakazIndex',
  components: { Top, ZakazForm },
  data() {
    return {
      loading: false,
      showDeleteModal: false,
      selectedOrder: null,
      orders: [],
      filterStatus: 'all',
      searchQuery: '',
      expandedId: null,
      showNewOrderModal: false,
      // Buyurtma statuslari (qo'lda o'zgartirish uchun)
      statusDefs: [
        { value: 'new', label: 'Yangi' },
        { value: 'process', label: 'Jarayon' },
        { value: 'completed', label: 'Tugallangan' },
        { value: 'paid', label: "Pul to'langan" },
        { value: 'canceled', label: 'Bekor qilingan' },
      ],
    };
  },
  computed: {
    // Qo'lda tanlanadigan statuslar — "Pul to'langan" avtomatik qo'yilgani uchun ro'yxatdan chiqarilgan
    selectableStatuses() {
      return this.statusDefs.filter(s => s.value !== 'paid');
    },
    filteredOrders() {
      let rows = this.orders;
      if (this.filterStatus !== 'all') {
        rows = rows.filter(o => o.status === this.filterStatus);
      }
      if (this.searchQuery && this.searchQuery.length > 1) {
        const q = this.searchQuery.toLowerCase();
        rows = rows.filter(o =>
          (o.kontragent && o.kontragent.name && o.kontragent.name.toLowerCase().includes(q)) ||
          (o.kontragent && o.kontragent.phone_number && String(o.kontragent.phone_number).includes(q)) ||
          (o.phone_number && String(o.phone_number).includes(q)) ||
          (o.address && o.address.toLowerCase().includes(q)) ||
          String(o.id).includes(q)
        );
      }
      return rows;
    },
  },
  mounted() {
    this.fetchOrders();
  },
  methods: {
    async fetchOrders() {
      this.loading = true;
      try {
        const response = await axios.get('/api/v1/bron');
        this.orders = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Ma'lumot olishda xato:", error);
      } finally {
        this.loading = false;
      }
    },
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    goToPOS(item) {
      if (Number(item.has_sale)) {
        alert("Bu buyurtma avval savdoga jo'natilgan");
        return;
      }
      localStorage.setItem('selected_bron_id', item.id);
      this.$router.push('/chiqim/pos');
    },
    // Savdolar ro'yxatidagi kabi — to'g'ridan-to'g'ri popup oynada 80mm chek chop etish
    async printOrder(item) {
      try {
        const res = await axios.get('/api/v1/bron/print/' + item.id);
        const order = res.data;
        if (!order) {
          alert("Buyurtma topilmadi");
          return;
        }

        const header = await this.fetchSkladHeader(order);
        const clientName = order.kontragent ? order.kontragent.name : "NOMA'LUM";
        const clientPhone = (order.kontragent && order.kontragent.phone_number) || order.phone_number || '—';
        const date = this.formatDate(order.created_at);

        // Xizmatga tegishli mahsulotlarning product_id to'plami (mustaqil mahsulotlardan ajratish uchun)
        const serviceMaterialIds = new Set();
        (order.services || []).forEach(s => {
          (s.items || []).forEach(it => { if (it.product_id != null) serviceMaterialIds.add(it.product_id); });
        });

        // Xizmatlarni guruhlash (package_id bo'yicha) + materiallari
        const groupMap = new Map();
        (order.services || []).forEach(s => {
          const key = s.package_id != null ? 'p' + s.package_id : 'n' + (s.name || '');
          if (!groupMap.has(key)) groupMap.set(key, { name: s.name || '—', count: 0, items: s.items || [] });
          groupMap.get(key).count += 1;
        });
        const serviceGroups = Array.from(groupMap.values()).map(g => ({
          name: g.name,
          count: g.count,
          materials: (g.items || []).map(it => ({
            name: it.product ? (it.product.extra_name || it.product.name || '—') : '—',
            qty: Number(it.quantity || 1) * g.count,
          })),
        }));

        // Faqat mustaqil mahsulotlar (xizmatga tegishli bo'lmaganlari)
        const standaloneProducts = (order.products || [])
          .filter(p => !serviceMaterialIds.has(p.product_id))
          .map(p => ({ name: p.product ? (p.product.extra_name || p.product.name || '—') : '—', count: Number(p.count || 0) }));

        let servicesHtml = '';
        if (serviceGroups.length > 0) {
          servicesHtml += `<div class="receipt-divider"></div>
            <div class="section-title">XIZMATLAR</div>
            <div class="receipt-divider-solid"></div>`;
          serviceGroups.forEach((s, i) => {
            servicesHtml += `<div class="service-block">
              <div class="item-row">
                <span class="ir-name">${i + 1}. ${s.name}</span>
                ${s.count > 1 ? `<span class="ir-qty">x${s.count}</span>` : ''}
              </div>
              ${s.materials.map(m => `<div class="material-row"><span class="mr-name">&#8627; ${m.name}</span><span class="mr-qty">${m.qty}</span></div>`).join('')}
            </div>`;
          });
        }

        let productsHtml = '';
        if (standaloneProducts.length > 0) {
          productsHtml += `<div class="receipt-divider"></div>
            <div class="section-title">MAHSULOTLAR</div>
            <div class="receipt-divider-solid"></div>`;
          standaloneProducts.forEach((p, i) => {
            productsHtml += `<div class="item-row"><span class="ir-name">${i + 1}. ${p.name}</span><span class="ir-qty">${p.count}</span></div>`;
          });
        }

        const emptyHtml = (serviceGroups.length === 0 && standaloneProducts.length === 0)
          ? `<div class="receipt-empty">Buyurtmada xizmat ham, mahsulot ham yo'q.</div>` : '';

        const logoHtml = header.logoUrl ? `<img src="${header.logoUrl}" class="receipt-logo" alt="logo" />` : '';
        const addressHtml = order.address ? `<div class="info-row"><span class="info-label">Manzil:</span><span class="info-value">${order.address}</span></div>` : '';
        const commentHtml = order.comment ? `<div class="receipt-divider"></div><div class="receipt-comment">${order.comment}</div>` : '';
        const checkWordHtml = header.checkWord ? `<div class="check-word">${header.checkWord}</div>` : '';

        const printWindow = window.open('', '_blank', 'width=320,height=600');
        printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Buyurtma cheki #${order.id}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    width: 80mm; font-family: 'Arial', 'Helvetica', sans-serif;
    font-size: 12px; font-weight: 700; color: #000; padding: 3mm;
    -webkit-font-smoothing: antialiased;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .receipt-logo { display: block; max-width: 60mm; max-height: 22mm; margin: 0 auto 2mm; object-fit: contain; }
  .receipt-header { text-align: center; margin-bottom: 2mm; }
  .firm-name { font-size: 16px; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; }
  .firm-phone { font-size: 11px; margin-top: 1mm; }
  .doc-type { font-size: 11px; font-weight: 700; margin-top: 1mm; }
  .receipt-date { font-size: 11px; margin-top: 1mm; }
  .receipt-divider { border-top: 1px dashed #000; margin: 2mm 0; }
  .receipt-divider-solid { border-top: 1px solid #000; margin: 1mm 0; }
  .receipt-info { margin: 1mm 0; }
  .info-row { display: flex; justify-content: space-between; font-size: 12px; line-height: 1.5; gap: 6px; }
  .info-label { color: #000; }
  .info-value { font-weight: 700; text-align: right; max-width: 60%; word-break: break-word; }
  .receipt-comment { font-size: 11px; font-style: italic; text-align: center; white-space: pre-wrap; }
  .section-title { text-align: center; font-weight: 800; letter-spacing: 1px; font-size: 12px; }
  .service-block { margin-bottom: 1mm; }
  .item-row { display: flex; justify-content: space-between; gap: 4px; align-items: baseline; font-size: 12px; line-height: 1.5; padding: 1.5mm 0; border-bottom: 2px dashed #000; }
  .ir-name { font-weight: 700; word-break: break-word; overflow-wrap: anywhere; }
  .ir-qty { font-weight: 700; white-space: nowrap; }
  .material-row { display: flex; justify-content: space-between; gap: 4px; padding: 1mm 0 1mm 3mm; font-size: 11px; line-height: 1.4; border-bottom: 1px dashed #000; }
  .mr-name { word-break: break-word; overflow-wrap: anywhere; }
  .mr-qty { white-space: nowrap; }
  .receipt-empty { text-align: center; font-style: italic; padding: 3mm 0; }
  .receipt-footer { text-align: center; margin-top: 2mm; padding-bottom: 3mm; }
  .check-word { font-weight: 800; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
  .thank-you { font-weight: 800; font-size: 13px; margin-top: 1mm; }
</style>
</head>
<body>
  <div class="receipt-header">
    ${logoHtml}
    <div class="firm-name">${header.firmName || 'PROFCAR'}</div>
    ${header.phone ? '<div class="firm-phone">Tel: ' + header.phone + '</div>' : ''}
    <div class="doc-type">BUYURTMA CHEKI</div>
    <div class="receipt-date">${date}</div>
  </div>
  <div class="receipt-divider"></div>
  <div class="receipt-info">
    <div class="info-row"><span class="info-label">№ buyurtma:</span><span class="info-value">${order.id}</span></div>
    <div class="info-row"><span class="info-label">Mijoz:</span><span class="info-value">${clientName}</span></div>
    <div class="info-row"><span class="info-label">Tel:</span><span class="info-value">${clientPhone}</span></div>
    ${addressHtml}
  </div>
  ${commentHtml}
  ${servicesHtml}
  ${productsHtml}
  ${emptyHtml}
  <div class="receipt-divider"></div>
  <div class="receipt-footer">
    ${checkWordHtml}
    <div class="thank-you">Rahmat!</div>
  </div>
</body>
</html>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
      } catch (e) {
        console.error('Chekni chiqarishda xato:', e);
        alert("Chekni chiqarishda xatolik yuz berdi");
      }
    },
    // Sklad (firma nomi/telefon/logo/chek izohi) sozlamalarini olish — savdo POS bilan bir xil
    async fetchSkladHeader(order) {
      const result = { firmName: '', phone: '', logoUrl: '', checkWord: '' };
      const sklad_id = (order && order.sklad_id) || localStorage.getItem('sklad_id');
      if (!sklad_id) return result;
      try {
        const [skladRes, settingsRes] = await Promise.all([
          axios.get(`/api/v1/sklads/id/${sklad_id}`),
          axios.post('/api/v1/sklad-settings', { sklad_id, key: 'a4_printer' }),
        ]);
        const sklad = (skladRes.data && (skladRes.data.data || skladRes.data)) || {};
        result.phone = sklad.phone_number || '';
        result.checkWord = sklad.check_word || '';
        const printerData = settingsRes.data && settingsRes.data.data;
        if (printerData) {
          const parsed = JSON.parse(printerData);
          result.firmName = (parsed && parsed.sender && parsed.sender.firm_name) || sklad.name || '';
          if (parsed && parsed.file_name) {
            result.logoUrl = axios.defaults.baseURL + 'api/v1/uploads/' + parsed.file_name;
          }
        } else {
          result.firmName = sklad.name || '';
        }
      } catch (e) {
        // sozlama topilmasa, sklad nomisiz davom etamiz
      }
      return result;
    },
    formatMoney(v) {
      if (!v) return '0';
      return Number(v).toLocaleString('ru-RU').replace(/ /g, ' ');
    },
    formatDate(ts) {
      if (!ts) return '-';
      return this.$formatDate(ts);
    },
    getStatusLabel(status) {
      const s = this.statusDefs.find(x => x.value === status);
      return s ? s.label : (status || '-');
    },
    statusClass(status) {
      const map = {
        new: 'st-new',
        process: 'st-process',
        completed: 'st-completed',
        paid: 'st-paid',
        canceled: 'st-canceled',
      };
      return map[status] || 'st-new';
    },
    // Statusni qo'lda o'zgartirish (backendga yengil PATCH)
    async changeStatus(item, status) {
      if (!status || status === item.status) return;
      // "Pul to'langan" statusi qo'lda qo'yilmaydi — faqat savdoga jo'natilib puli to'langanda avtomatik
      if (status === 'paid') return;
      const prev = item.status;
      item.status = status; // optimistik yangilash
      try {
        await axios.patch(`/api/v1/bron/status/${item.id}`, { status });
      } catch (e) {
        item.status = prev; // xatoda qaytaramiz
        alert("Statusni o'zgartirishda xatolik yuz berdi");
      }
    },
    openNewOrderModal() {
      this.showNewOrderModal = true;
    },
    openDeleteModal(item) {
      this.selectedOrder = item;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedOrder = null;
    },
    async deleteItem() {
      try {
        await axios.delete(`/api/v1/bron/id/${this.selectedOrder.id}`);
        this.orders = this.orders.filter(o => o.id !== this.selectedOrder.id);
        this.closeDeleteModal();
      } catch (error) {
        alert("O'chirishda xatolik yuz berdi");
      }
    },
  }
};
</script>

<style scoped>
.g-page {
  padding: 20px;
  background: #f8fafc;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.content-container {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.page-title {
  color: #a142f4;
  font-weight: 800;
  font-size: 26px;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* SEARCH */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
}

.modern-search {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 8px 15px 8px 35px;
  border-radius: 12px;
  width: 220px;
  outline: none;
  transition: 0.3s;
  font-weight: 500;
}

.modern-search:focus {
  border-color: #a142f4;
  box-shadow: 0 0 0 4px rgba(161, 66, 244, 0.1);
}

/* STATUS FILTER (segmented) */
.status-filter {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 12px;
  padding: 4px;
}

.filter-btn {
  padding: 6px 14px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: 0.2s;
}

.filter-btn.active {
  background: #a142f4;
  color: white;
  box-shadow: 0 2px 8px rgba(161, 66, 244, 0.25);
}

/* REFRESH */
.refresh-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.refresh-btn:hover {
  border-color: #a142f4;
  color: #a142f4;
}

.table-card {
  background: white;
  border-radius: 24px;
  padding: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.table-wrapper {
  overflow-y: auto;
  flex-grow: 1;
}

.custom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.custom-table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 5;
  padding: 18px 15px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  border-bottom: 1px solid #f1f5f9;
}

.custom-table td {
  padding: 14px 15px;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
  font-size: 15px;
}

.main-row { cursor: pointer; transition: background 0.15s; }
.main-row:hover td { background: #fafbff; }
.main-row.expanded td { background: #f8f5ff; }

.detail-row td {
  background: #f9fafb;
  padding: 0 !important;
  border-bottom: 2px solid #e2e8f0;
}

.detail-container {
  padding: 16px 20px;
}

.comment-block {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  color: #92400e;
  margin-bottom: 12px;
}

.products-header {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

.inner-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.inner-table th {
  text-align: left;
  padding: 8px 10px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.inner-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.no-products {
  text-align: center;
  color: #94a3b8;
  padding: 12px;
  font-size: 13px;
}

.id-badge {
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 700;
  color: #64748b;
  font-size: 13px;
}

.price.blue { color: #2563eb; font-weight: 800; }

.tag {
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  display: inline-block;
}

.tag.grey     { background: #f1f5f9; color: #475569; }
.tag.purple   { background: #f5f3ff; color: #a142f4; }
.tag.green-tag { background: #ecfdf5; color: #10b981; }

/* Status — qo'lda o'zgartiriladigan select (rangli badge ko'rinishida) */
.status-select-inline {
  border: 1.5px solid transparent;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 16 16'><path fill='%23999' d='M4 6l4 4 4-4z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 8px center;
  padding-right: 24px;
  transition: 0.15s;
}
.status-select-inline.st-new       { background-color: #f5f3ff; color: #7c3aed; border-color: #ddd6fe; }
.status-select-inline.st-process   { background-color: #fff7ed; color: #ea580c; border-color: #fed7aa; }
.status-select-inline.st-completed { background-color: #ecfdf5; color: #059669; border-color: #a7f3d0; }
.status-select-inline.st-paid      { background-color: #eff6ff; color: #2563eb; border-color: #bfdbfe; }
.status-select-inline.st-canceled  { background-color: #fff1f2; color: #e11d48; border-color: #fecdd3; }
.status-select-inline:focus { box-shadow: 0 0 0 3px rgba(161, 66, 244, 0.12); }

/* Avtomatik (o'zgartirilmaydigan) status — masalan "Pul to'langan" */
.status-badge {
  display: inline-block;
  border: 1.5px solid transparent;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
}
.status-badge.st-paid { background-color: #eff6ff; color: #2563eb; border-color: #bfdbfe; }

.actions { display: flex; gap: 8px; justify-content: center; align-items: center; }

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
}

.view  { background: #f5f3ff; color: #a142f4; }
.print { background: #ecfeff; color: #0891b2; }
.del   { background: #fff1f2; color: #e11d48; }

.print:hover { background: #0891b2; color: white; transform: scale(1.05); }

.action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: none;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s;
}

.pos-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 3px 10px rgba(16, 185, 129, 0.3);
}

.pos-btn:hover { box-shadow: 0 5px 15px rgba(16, 185, 129, 0.4); }

.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(15, 23, 42, 0.3);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: white;
  padding: 35px;
  border-radius: 28px;
  width: 420px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
}

.warning-icon {
  width: 60px; height: 60px;
  background: #fee2e2; color: #ef4444;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 30px; font-weight: bold;
  margin: 0 auto 20px;
}

.modal-btns { display: flex; gap: 12px; margin-top: 30px; }

.m-btn {
  flex: 1;
  padding: 14px;
  border-radius: 14px;
  border: none;
  font-weight: 700;
  cursor: pointer;
}

.cancel  { background: #f1f5f9; color: #64748b; }
.confirm { background: #e11d48; color: white; }

.animate-pop { animation: pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }

@keyframes pop {
  0%   { transform: scale(0.9); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.font-bold { font-weight: 700; }
.text-dark { color: #1e293b; }
.text-muted { color: #94a3b8; }
.small { font-size: 12px; }

/* Yangi buyurtma tugmasi */
.new-order-btn {
  background: linear-gradient(135deg, #a142f4 0%, #8b2cf5 100%);
  color: white;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-weight: 700;
  box-shadow: 0 4px 15px rgba(161, 66, 244, 0.3);
  transition: 0.2s;
}
.new-order-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(161, 66, 244, 0.4);
}

@media (max-width: 1200px) {
  .modern-search {
    width: 180px;
  }
}

@media (max-width: 900px) {
  .header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .modern-search {
    width: 100%;
    flex: 1;
  }
}

@media (max-width: 600px) {
  .status-filter {
    flex-wrap: wrap;
  }
}
</style>
